import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { get } from 'lodash';
import {
  MimeTypeHelper,
  Field2 as Field,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import {
  AddCircle,
  Add,
  Edit,
} from '@mui/icons-material';
import defaultPlaylistImage from '../../../../assets/default_playlist.png';

const categories = [
  {
    label: '',
    value: '',
  },
  {
    label: 'Blues',
    value: 'blues',
  },
  {
    label: 'Country',
    value: 'country',
  },
  {
    label: 'Children',
    value: 'children',
  },
  {
    label: 'Dance/Electronic',
    value: 'dance/electronic',
  },
  {
    label: 'Holiday',
    value: 'holiday',
  },
  {
    label: 'Instrumental',
    value: 'instrumental',
  },
  {
    label: 'Jazz',
    value: 'jazz',
  },
  {
    label: 'Misc',
    value: 'misc',
  },
  {
    label: 'Pop',
    value: 'pop',
  },
  {
    label: 'Pop/Rock',
    value: 'pop/rock',
  },
  {
    label: 'Rock',
    value: 'rock',
  },
  {
    label: 'Rap',
    value: 'rap',
  },
  {
    label: 'R&B',
    value: 'r&b',
  },
  {
    label: 'Religion',
    value: 'religion',
  },
  {
    label: 'Specialty',
    value: 'specialty',
  },
  {
    label: 'Variety',
    value: 'variety',
  },
  {
    label: 'World',
    value: 'world',
  },
];

class Information extends PureComponent {
  /**
   * When a field is changed
   */
  onChange = (e) => {
    const {
      target: {
        name,
        value,
      },
    } = e;

    const {
      onUpdate,
    } = this.props;

    onUpdate({
      [name]: value,
    });
  };

  /**
   * When the tag field is changed
   */
  onChangeTag = (data) => {
    const {
      onUpdate,
    } = this.props;

    onUpdate(data);
  };

  /**
   * When the company field is changed
   */
  onSetCompany = ({
    id: companyId,
    value: companyName,
  }) => {
    const {
      companyId: currCompanyId,
      tags,
      onUpdate,
    } = this.props;

    const data = !companyId
      ? {
        allLocations: false,
        locations: [],
        locationsData: {
          companies: [],
          locations: [],
        },
        tags: currCompanyId !== companyId
          ? tags.filter(tag => tag.type === 'admin')
          : tags,
      }
      : {};

    onUpdate({
      companyId,
      companyName,
      ...data,
    });
  };

  onDrop = (acceptedFiles) => {
    const file = get(acceptedFiles, '[0]', false);

    if (file) {
      const {
        onUpdate,
        onUploadImage,
      } = this.props;

      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        // Save image data to state to show as a preview
        onUpdate({ imageUrl: fileAsDataURL });
      };

      reader.onabort = () => console.error('file reading was aborted');
      reader.onerror = () => console.error('file reading has failed');

      reader.readAsDataURL(file);

      onUploadImage(file);
    }
  };

  render() {
    const {
      name,
      category,
      description,
      image: playlistImage,
      companyName,
      companyId,
      tags,
      disableInput,
      user,
      isNew,
      isDuplicate,
    } = this.props;

    const canSetCompany = user.sysAdmin;

    return (
      <Grid className="Information" spacing={2} container>
        <Grid size={3}>
          <div className="playlist-image">
            <Dropzone
              accept={MimeTypeHelper.images}
              multiple={false}
              onDrop={this.onDrop}
            >
              {({ getRootProps, getInputProps, isDragActive }) => {
                return (
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'dropzone--isActive' : ''}`}
                  >
                    <input {...getInputProps()} />

                    {playlistImage ? (
                      <img src={playlistImage} alt="Playlist Preview" />
                    ) : (
                      <img src={defaultPlaylistImage} alt="Playlist" />
                    )}

                    {isDragActive ? (
                      <div className="icon-drop">
                        <AddCircle className="icon" />
                      </div>
                    ) : null}

                    <div className="icon-container">
                      {isDragActive ? (
                        <Add className="icon" />
                      ) : (
                        <Edit className="icon" />
                      )}
                    </div>
                  </div>
                );
              }}
            </Dropzone>
          </div>
        </Grid>

        <Grid size={9}>
          <Grid spacing={2} container>
            <Grid size={6}>
              <Field
                type="text"
                label="Name"
                name="name"
                placeholder="Name"
                value={name}
                tabIndex={1}
                disabled={disableInput}
                testId="playlist-name-input"
                onChange={this.onChange}
                required
                autoFocus
              />
            </Grid>

            <Grid size={6}>
              {canSetCompany && (
                <div className="playlist-company">
                  <div className="form-container">
                    <Field
                      type="company"
                      label="Company"
                      name="companyName"
                      value={companyName}
                      tabIndex={2}
                      companyProps={{
                        companyId,
                        companyName,
                      }}
                      disabled={disableInput || (!isNew && !isDuplicate)}
                      testId="playlist-company-input"
                      onChange={this.onSetCompany}
                      required
                    />
                  </div>
                </div>
              )}
            </Grid>

            <Grid size={12}>
              <Field
                type="select"
                label="Genre"
                name="category"
                value={category}
                options={categories}
                tabIndex={3}
                disabled={disableInput}
                testId="playlist-genre-select"
                onChange={this.onChange}
                required
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12}>
          <Field
            style={{
              height: 60,
            }}
            type="textarea"
            label="Description"
            name="description"
            placeholder="Description"
            value={description}
            tabIndex={4}
            disabled={disableInput}
            testId="playlist-description-input"
            onChange={this.onChange}
            required
          />
        </Grid>

        <Grid size={12}>
          <Field
            type="tags"
            label="Tags"
            name="tags"
            tagProps={{
              companyId,
              tags,
              allowTypes: ['client', 'admin'],
              showAs: 'list',
              assign: user.can('playlist.assign_tag'),
            }}
            disabled={disableInput}
            testId="playlist-tags-input"
            onChange={this.onChangeTag}
            tabIndex={5}
          />
        </Grid>
      </Grid>
    );
  }
}

Information.propTypes = {
  name: PropTypes.string,
  category: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  companyName: PropTypes.string,
  companyId: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.object),
  disableInput: PropTypes.bool,
  isNew: PropTypes.bool,
  isDuplicate: PropTypes.bool,
  onUploadImage: PropTypes.func,
  onUpdate: PropTypes.func,
};

Information.defaultProps = {
  name: '',
  category: '',
  description: '',
  image: '',
  companyName: '',
  companyId: '',
  tags: [],
  disableInput: false,
  isNew: false,
  isDuplicate: false,
  onUploadImage: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

export default connect(mapStateToProps)(Information);
